import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const AUDIENCES = [
  {
    tag: "FOUNDERS",
    title: "Build the business behind the idea.",
    copy: "For founders who need clarity, product/service strategy, prioritization, execution support and a stronger operating foundation.",
    examples: ["Founder strategy", "Product/service strategy", "Prioritization", "MVPs", "Founder OS", "AI strategy"],
    cta: "Explore Founder Support",
    to: "/founders",
    testid: "who-i-help-founders",
  },
  {
    tag: "SERVICE BUSINESSES",
    title: "Build a more scalable way of working.",
    copy: "For service businesses looking to improve processes, strengthen operations, use AI and automation effectively, and create a more scalable operating model.",
    examples: ["Business strategy", "Process improvement", "Operating systems", "AI readiness", "Automation", "Fractional Product Management"],
    cta: "Explore Business Support",
    to: "/businesses",
    testid: "who-i-help-businesses",
  },
  {
    tag: "NONPROFITS",
    title: "Strengthen the organization behind the mission.",
    copy: "For nonprofits and community organizations looking to strengthen programs, processes, leadership and organizational capacity.",
    examples: ["Program management", "Program strategy", "Process improvement", "Capacity building", "Facilitation", "Practical AI"],
    cta: "Explore Nonprofit Support",
    to: "/nonprofits",
    testid: "who-i-help-nonprofits",
  },
];

export default function WhoIHelp() {
  return (
    <section
      id="who-i-help"
      data-testid="who-i-help-section"
      className="scroll-mt-20 py-20 sm:py-28 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          chapter="03 — Who I Help"
          title="Different organizations. Same need: a better way forward."
          testid="who-i-help-head"
        />
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.tag} delay={i * 0.1} className="h-full">
              <article
                data-testid={a.testid}
                className="group flex h-full flex-col border border-line bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber/60 sm:p-10"
              >
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">
                  {a.tag}
                </p>
                <h3 className="mt-5 font-serif text-2xl font-normal leading-snug text-ink sm:text-[1.7rem]">
                  {a.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-body sm:text-base">
                  {a.copy}
                </p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {a.examples.map((ex) => (
                    <li
                      key={ex}
                      className="border border-line bg-paper px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-body"
                    >
                      {ex}
                    </li>
                  ))}
                </ul>
                <Link
                  data-testid={`${a.testid}-cta`}
                  to={a.to}
                  className="mt-auto inline-flex items-center gap-2 pt-8 text-sm font-semibold text-ink transition-colors duration-300 group-hover:text-amber"
                >
                  {a.cta}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
